import React from "react";
import { Modal, ModalBody } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./ChannelFormModal.css";

export const DEFAULT_CHANNEL_FORM = {
  name: "",
  desc: "",
  enable: "true",
  transcoder: "true",
  rec_profile: "1080p",
  option: "none",
  hls_option: "hls",
};

export const getDefaultChannelForm = () => ({ ...DEFAULT_CHANNEL_FORM });

const StatusSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    className={`channel-form__switch${checked ? " is-on" : ""}`}
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
  >
    <span className="channel-form__switch-thumb" />
  </button>
);

const SelectGroup = ({ label, value, options, onChange, disabled }) => (
  <div className="channel-form__group">
    <label className="channel-form__label">{label}</label>
    <div className="channel-form__select-wrap">
      <select
        className="channel-form__select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const ChannelFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  values,
  onChange,
  submitting = false,
  mode = "create",
  serviceType = "live",
}) => {
  const isCreate = mode === "create";
  const isRadio = serviceType === "radio";
  const cdnDisabled = values.transcoder === "false";
  const titlePrefix = serviceType === "radio" ? "Radio" : "Channel";
  const namePlaceholder =
    serviceType === "radio" ? "Radio name" : "Channel name";

  const handleTranscoderChange = (nextValue) => {
    onChange({
      ...values,
      transcoder: nextValue,
      ...(nextValue === "false" ? { hls_option: "hls" } : {}),
    });
  };

  const handleHlsChange = (nextValue) => {
    onChange({
      ...values,
      hls_option: nextValue,
      ...(nextValue === "ll-hls" ? { transcoder: "true" } : {}),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      backdrop="static"
      size="lg"
      className="channel-form-modal"
      modalClassName="channel-form-modal__dialog"
      contentClassName="channel-form-modal__content"
    >
      <div className="channel-form-modal__header">
        <h3 className="channel-form-modal__title">
          {titlePrefix} &gt; {isCreate ? "Create" : "Edit"}
        </h3>
        <button
          type="button"
          className="channel-form-modal__close"
          onClick={onClose}
          aria-label="Close"
          disabled={submitting}
        >
          <FontAwesomeIcon icon={["fas", "times"]} />
        </button>
      </div>

      <ModalBody className="channel-form__body">
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="channel-form__grid">
            <div className="channel-form__column">
              <div className="channel-form__group">
                <label className="channel-form__label" htmlFor="channel-name">
                  Name <span className="channel-form__required">*</span>
                </label>
                <input
                  id="channel-name"
                  type="text"
                  className="channel-form__input"
                  placeholder={namePlaceholder}
                  value={values.name}
                  onChange={(event) =>
                    onChange({ ...values, name: event.target.value })
                  }
                  disabled={submitting}
                />
              </div>

              <div className="channel-form__group">
                <label className="channel-form__label" htmlFor="channel-desc">
                  Description
                </label>
                <textarea
                  id="channel-desc"
                  className="channel-form__textarea"
                  rows="4"
                  placeholder="Optional description"
                  value={values.desc}
                  onChange={(event) =>
                    onChange({ ...values, desc: event.target.value })
                  }
                  disabled={submitting}
                />
              </div>

              <div className="channel-form__group">
                <label className="channel-form__label">Status</label>
                <StatusSwitch
                  checked={values.enable === "true"}
                  disabled={submitting}
                  onChange={(on) =>
                    onChange({ ...values, enable: on ? "true" : "false" })
                  }
                />
              </div>
            </div>

            <div className="channel-form__column">
              <SelectGroup
                label="Live Transcoder"
                value={values.transcoder}
                onChange={handleTranscoderChange}
                disabled={submitting}
                options={[
                  { value: "true", label: "Enable" },
                  { value: "false", label: "Disable" },
                ]}
              />

              {!isRadio && (
                <SelectGroup
                  label="Video Quality"
                  value={values.rec_profile}
                  onChange={(rec_profile) =>
                    onChange({ ...values, rec_profile })
                  }
                  disabled={submitting}
                  options={[
                    { value: "1080p", label: "1080p" },
                    { value: "720p", label: "720p" },
                  ]}
                />
              )}

              {!isCreate && (
                <SelectGroup
                  label="Recorder Mode"
                  value={values.option}
                  onChange={(option) => onChange({ ...values, option })}
                  disabled={submitting}
                  options={[
                    { value: "auto", label: "Auto" },
                    { value: "manual", label: "Manual" },
                    { value: "none", label: "Off" },
                  ]}
                />
              )}

              <SelectGroup
                label="CDN EDGE"
                value={values.hls_option}
                onChange={handleHlsChange}
                disabled={submitting}
                options={[
                  { value: "hls", label: "HLS" },
                  { value: "ll-hls", label: "LL-HLS", disabled: cdnDisabled },
                ]}
              />
            </div>
          </div>
        </form>
      </ModalBody>

      <div className="channel-form-modal__footer">
        <button
          type="button"
          className="channel-form-modal__btn channel-form-modal__btn--primary"
          onClick={onSubmit}
          disabled={submitting}
        >
          <FontAwesomeIcon icon={["fas", "save"]} />
          {submitting ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          className="channel-form-modal__btn channel-form-modal__btn--ghost"
          onClick={onClose}
          disabled={submitting}
        >
          <FontAwesomeIcon icon={["fas", "times"]} />
          Close
        </button>
      </div>
    </Modal>
  );
};

export default ChannelFormModal;
